/**
 * Portal Services Page
 * Service management for PFEMP, PFGOV, PFPRO users
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import portalService from '../../services/portal.service';
import type { PortalService as PortalServiceType } from '../../types/portal.types';

const PortalServices: React.FC = () => {
  const { user } = useAuth();
  const [services, setServices] = useState<PortalServiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isGovUser = user?.role === 'PFGOV';

  const loadData = async () => {
    try {
      setLoading(true);
      const servicesRes = await portalService.getServices();
      setServices(servicesRes.data || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar serviços');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleToggle = async (service: PortalServiceType) => {
    try {
      await portalService.toggleService(service.ENTIDADE_SERVICO_ID);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Erro ao alterar estado do servico');
    }
  };

  const getEstadoLabel = (estado: string) => {
    switch (estado) {
      case 'A': return { text: 'Ativo', class: 'bg-green-100 text-green-800' };
      case 'I': return { text: 'Inativo', class: 'bg-gray-100 text-gray-800' };
      case 'P': return { text: 'Pendente', class: 'bg-yellow-100 text-yellow-800' };
      default: return { text: estado, class: 'bg-gray-100 text-gray-800' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pagali-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isGovUser ? 'Serviços Governamentais' : 'Meus Serviços'}
          </h1>
          <p className="text-gray-600">Gerir os serviços oferecidos</p>
        </div>
        {/* Criação de serviços gerida pelo Admin */}
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="text-sm text-red-500 underline mt-1">
            Fechar
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total Serviços</p>
          <p className="text-2xl font-semibold">{services.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Ativos</p>
          <p className="text-2xl font-semibold text-green-600">
            {services.filter((s) => s.SERVICO_ESTADO === 'A').length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Inativos</p>
          <p className="text-2xl font-semibold text-gray-400">
            {services.filter((s) => s.SERVICO_ESTADO !== 'A').length}
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.length === 0 ? (
          <div className="col-span-full bg-white rounded-lg shadow p-12 text-center text-gray-500">
            Nenhum serviço encontrado. Contacte o administrador para adicionar serviços.
          </div>
        ) : (
          services.map((service) => {
            const estado = getEstadoLabel(service.SERVICO_ESTADO);
            return (
              <div key={service.ENTIDADE_SERVICO_ID} className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center">
                      {service.LOGO ? (
                        <img
                          src={`data:${service.MIME_TYPE};base64,${service.LOGO}`}
                          alt={service.DESIG}
                          className="h-12 w-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-lg bg-pagali-primary/10 flex items-center justify-center">
                          <svg className="w-6 h-6 text-pagali-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-gray-900">{service.DESIG}</h3>
                        {service.CODIGO && (
                          <p className="text-sm text-gray-500">Codigo: {service.CODIGO}</p>
                        )}
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${estado.class}`}>
                      {estado.text}
                    </span>
                  </div>
                  {service.DESCR && (
                    <p className="mt-4 text-sm text-gray-600 line-clamp-2">{service.DESCR}</p>
                  )}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {service.CATEGORIA && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        {service.CATEGORIA}
                      </span>
                    )}
                    {service.TIPO_SERVICO && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded">
                        {service.TIPO_SERVICO}
                      </span>
                    )}
                  </div>
                </div>
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
                  <button
                    onClick={() => handleToggle(service)}
                    className={`text-sm ${service.SERVICO_ESTADO === 'A' ? 'text-orange-600' : 'text-green-600'}`}
                  >
                    {service.SERVICO_ESTADO === 'A' ? 'Desativar' : 'Ativar'}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

    </div>
  );
};

export default PortalServices;
